/**
 * Reusable Modal Footer Component
 *
 * Cancel + primary action for every modal built on BudgetModalTemplate.
 *
 * Renders plain Semantic <Button>, which is the whole point: the app's Semantic theme already
 * defines what a button looks like here — the 15px pill from @defaultBorderRadius, the #08D6A1
 * primary from @primaryColor, the filled grey secondary, the bold weight and the padding.
 *
 * It used to render native <button className="budget-button">, which reimplemented all of that
 * at 4.8px with a transparent Cancel, a fixed 32px height and a fixed 120px width. That is why
 * seven dialogs in issue #126 looked unlike the Add Supplier / Add Sponsor dialogs the ticket
 * holds up as correct — those two are plain <Button> and nothing else.
 *
 * No wrapper div. Semantic's Modal.Actions already sets text-align: right, which is how the
 * reference dialogs position theirs; adding a flex container on top would only reintroduce a
 * second opinion about spacing.
 *
 * @component
 * @example
 * ```tsx
 * <ModalFooter
 *   onCancel={handleCancel}
 *   onPrimaryAction={handleSave}
 *   primaryLabel="Save"
 *   primaryDisabled={!hasChanges}
 *   isLoading={isSaving}
 *   loadingLabel="Saving..."
 * />
 * ```
 */

import React from 'react';
import { Button } from 'semantic-ui-react';

export interface ModalFooterProps {
  /** Handler for cancel button click */
  onCancel: () => void;

  /** Handler for primary action button click */
  onPrimaryAction: () => void;

  /** Label for cancel button */
  cancelLabel?: string;

  /** Label for primary action button */
  primaryLabel: string;

  /** Whether primary action button is disabled */
  primaryDisabled?: boolean;

  /** Whether component is in loading state */
  isLoading?: boolean;

  /** Label to show during loading state */
  loadingLabel?: string;

  /** Additional content to render above buttons (e.g., error messages) */
  additionalContent?: React.ReactNode;
}

/**
 * Reusable modal footer with Cancel and Primary action buttons
 */
const ModalFooter: React.FC<ModalFooterProps> = ({
  onCancel,
  onPrimaryAction,
  cancelLabel = 'Cancel',
  primaryLabel,
  primaryDisabled = false,
  isLoading = false,
  loadingLabel,
  additionalContent
}) => {
  const isPrimaryEnabled = !primaryDisabled && !isLoading;
  const displayPrimaryLabel = isLoading && loadingLabel ? loadingLabel : primaryLabel;

  return (
    <>
      {additionalContent}
      <Button
        onClick={onCancel}
        disabled={isLoading}
        data-testid="cancel-button"
      >
        {cancelLabel}
      </Button>
      {/*
        Deliberately NOT Semantic's `loading` prop, which swaps the label for a spinner. The
        loadingLabel this already renders ("Saving…") says more than a spinner does, and callers
        pass it expecting to see it. Only the appearance changes here, never the behaviour.
      */}
      <Button
        primary
        onClick={onPrimaryAction}
        disabled={!isPrimaryEnabled}
        data-testid="save-button"
      >
        {displayPrimaryLabel}
      </Button>
    </>
  );
};

export default ModalFooter;
